// src/pages/events/components/event-form.tsx
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ImageUpload } from "@/components/ui/image-upload";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Event } from "@/lib/api/types";
import { Loader2 } from "lucide-react";

// Mirrors the API's event schema so problems show next to the field instead of as a toast
const buildSchema = (minSeats: number) =>
  z.object({
    title: z.string().trim().min(3, "Title must be at least 3 characters").max(100, "Title cannot exceed 100 characters"),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date is required"),
    location: z.string().trim().min(3, "Location must be at least 3 characters"),
    description: z.string().trim().min(10, "Description must be at least 10 characters"),
    seatsAvailable: z
      .number({ invalid_type_error: "Enter the number of seats" })
      .int("Seats must be a whole number")
      .min(Math.max(minSeats, 1), minSeats > 1 ? `${minSeats} people have already RSVP'd` : "Must have at least 1 seat"),
    fee: z
      .number({ invalid_type_error: "Enter a fee (0 for free)" })
      .int("Fee must be a whole number of KES")
      .min(0, "Fee cannot be negative"),
    googleMapsLink: z.union([z.literal(""), z.string().trim().url("Must be a valid URL")]),
    image: z.any().optional(),
  });

type FormValues = z.infer<ReturnType<typeof buildSchema>>;

interface EventFormProps {
  initialData?: Event | null;
  onSubmit: (data: FormData) => Promise<void>;
}

function FieldError({ message }: { message?: string }) {
  return message ? <p className="mt-1 text-sm text-destructive">{message}</p> : null;
}

export function EventForm({ initialData, onSubmit }: EventFormProps) {
  const rsvpCount = initialData?.rsvpCount ?? 0;

  const form = useForm<FormValues>({
    resolver: zodResolver(buildSchema(rsvpCount)),
    defaultValues: {
      title: initialData?.title || "",
      date: initialData?.date ? initialData.date.slice(0, 10) : "",
      location: initialData?.location || "",
      description: initialData?.description || "",
      seatsAvailable: initialData?.seatsAvailable || 50,
      fee: initialData?.fee || 0,
      googleMapsLink: initialData?.googleMapsLink || "",
    },
  });
  const { errors, isSubmitting } = form.formState;

  const handleSubmit = async (values: FormValues) => {
    const formData = new window.FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && key !== "image") {
        formData.append(key, String(value));
      }
    });
    if (values.image) {
      formData.append("image", values.image);
    }
    try {
      await onSubmit(formData);
    } catch {
      // Errors are surfaced by the API client's toast
    }
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5 text-foreground">
      <div>
        <Label htmlFor="title">
          Event Title <span className="text-destructive">*</span>
        </Label>
        <Input id="title" placeholder="e.g. Back-to-School Drive" {...form.register("title")} />
        <FieldError message={errors.title?.message} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="date">
            Event Date <span className="text-destructive">*</span>
          </Label>
          <Input id="date" type="date" {...form.register("date")} />
          <FieldError message={errors.date?.message} />
        </div>
        <div>
          <Label htmlFor="location">
            Location <span className="text-destructive">*</span>
          </Label>
          <Input id="location" placeholder="Venue, town" {...form.register("location")} />
          <FieldError message={errors.location?.message} />
        </div>
      </div>

      <div>
        <Label htmlFor="googleMapsLink">
          Google Maps Link <span className="text-xs font-normal text-muted-foreground">(optional)</span>
        </Label>
        <Input id="googleMapsLink" placeholder="https://maps.app.goo.gl/..." {...form.register("googleMapsLink")} />
        <FieldError message={errors.googleMapsLink?.message} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="seatsAvailable">
            Available Seats <span className="text-destructive">*</span>
          </Label>
          <Input
            id="seatsAvailable"
            type="number"
            min={Math.max(rsvpCount, 1)}
            {...form.register("seatsAvailable", { valueAsNumber: true })}
          />
          <FieldError message={errors.seatsAvailable?.message} />
          {rsvpCount > 0 && !errors.seatsAvailable && (
            <p className="mt-1 text-xs text-muted-foreground">{rsvpCount} already registered</p>
          )}
        </div>
        <div>
          <Label htmlFor="fee">Event Fee (KES)</Label>
          <Input id="fee" type="number" min={0} {...form.register("fee", { valueAsNumber: true })} />
          <FieldError message={errors.fee?.message} />
          {!errors.fee && <p className="mt-1 text-xs text-muted-foreground">Use 0 for a free event</p>}
        </div>
      </div>

      <div>
        <Label htmlFor="description">
          Description <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="description"
          rows={5}
          placeholder="What is the event about, and who should attend?"
          className="resize-y"
          {...form.register("description")}
        />
        <FieldError message={errors.description?.message} />
      </div>

      <div>
        <Label>Event Image</Label>
        <ImageUpload
          value={initialData?.imageUrl}
          onChange={(file) => form.setValue("image", file)}
          onRemove={() => form.setValue("image", undefined)}
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" disabled={isSubmitting} className="min-w-[140px]">
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : initialData ? (
            "Save Changes"
          ) : (
            "Create Event"
          )}
        </Button>
      </div>
    </form>
  );
}
